import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import ReportsFilters from '@/pages/reports/components/ReportsFilters';
import ReportsSummaryCards from '@/pages/reports/components/ReportsSummaryCards';
import VerticalBarsGrid from '@/pages/reports/components/VerticalBarsGrid';
import DailySeriesCombined from '@/pages/reports/components/DailySeriesCombined';
import DailyBars from '@/pages/reports/components/DailyBars';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { getReceptionReportTotals, getReceptionDaily } from '@/pages/reception/services/reception.services';
import { getWashingCycleReportTotals, getWashingCycleDaily, getWashingParticipationByMachine, fetchWashingCycles } from '@/pages/washingCycles/services/washingCycles.services';
import { getDryingCycleReportTotals, fetchDryingCycles } from '@/pages/dryingCycles/services/dryingCycles.services';
import { getUtilityReportTotals, getUtilityDaily } from '@/pages/reports/services/utilities.reports.services';
import { fetchLaundries } from '@/pages/parametrizacion/laundries/Services/laundries.services';
import useCentrosPermitidos from '@/hooks/useCentrosPermitidos';
import { getWorkedHoursDailyReport, getWorkedHoursSummary } from '@/pages/workedHours/services/workedHours.services';
import { getProductConsumptionTotals } from '@/pages/reports/services/productConsumption.reports.services';
import { getAlistamientosByLavanderia } from '@/pages/laundry/alistamientos/services/alistamientos.services';
import { sendReportEmail } from './services/email.reports.services';
import { sendReportByData } from './services/reportEmail.services';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Clock, CheckCircle, Gauge, BarChart3, Mail, Send, Loader2, ClipboardList, PackageCheck, Layers } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import EmailReportBuilder from '@/pages/reports/components/EmailReportBuilder';
import { fetchAdminUsers } from '@/pages/reports/services/adminUsers.reports.services';
import { getActiveHolidays } from '@/pages/reports/services/holidays.reports.services';

const toISODate = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString();
// Helper: interpreta solo la fecha (YYYY-MM-DD) de un ISO string y crea Date en hora local
const fromISODateOnly = (iso) => {
  const s = String(iso || '').slice(0, 10); // YYYY-MM-DD
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

const ReportsPage = () => {
  const { toast } = useToast();
  const { user, ROLES: R } = useAuth();

  // Filtros
  const today = new Date();
  // Fin de rango por defecto: ayer
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  // Inicio de rango: primer día del mes actual, pero si ayer es del mes anterior (p.ej. día 1),
  // usar primer día del mes anterior para mantener start <= end
  const firstDayCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const firstDayPrevMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const defaultStart = yesterday < firstDayCurrentMonth ? firstDayPrevMonth : firstDayCurrentMonth;
  const [startDate, setStartDate] = useState(toISODate(defaultStart));
  const [endDate, setEndDate] = useState(toISODate(yesterday));
  const [laundries, setLaundries] = useState([]);
  const { tieneRestriccion, puedeVerCentro, cargarCentros, centroPorDefecto } = useCentrosPermitidos();
  // Arranca en un centro que el usuario tenga permitido: su `lavanderia` puede
  // no estar entre los centros asignados.
  const [idLavanderia, setIdLavanderia] = useState(() => centroPorDefecto);
  const [selectedLaundry, setSelectedLaundry] = useState(null);

  // Resultados
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [receptionTotals, setReceptionTotals] = useState({ kilosProcesados: 0, kilosRechazo: 0, pendientes: 0, conteo: 0 });
  const [washingTotals, setWashingTotals] = useState({ kilosLavados: 0, ciclos: 0 });
  const [washingAbortedTotals, setWashingAbortedTotals] = useState(0);
  const [washingAbortedKilos, setWashingAbortedKilos] = useState(0);
  const [washingByMachine, setWashingByMachine] = useState([]);
  const [washingAbortedByMachine, setWashingAbortedByMachine] = useState([]);
  const [dryingTotals, setDryingTotals] = useState({ kilosSecados: 0, ciclos: 0 });
  const [dryingAbortedTotals, setDryingAbortedTotals] = useState(0);
  const [dryingAbortedKilos, setDryingAbortedKilos] = useState(0);
  const [utilityTotals, setUtilityTotals] = useState({ consumoLitros: 0, costoTotal: 0 });
  const [utilityTypeTotals, setUtilityTypeTotals] = useState({
    agua: { consumoLitros: 0, costos: 0 },
    gas: { consumoLitros: 0, costos: 0 },
    Electricidad: { consumoLitros: 0, costos: 0 },
    aguaCaliente: { consumoLitros: 0, costos: 0 },
  });
  const [utilityDailyFromApi, setUtilityDailyFromApi] = useState([]);
  const [utilityDailyTypesFromApi, setUtilityDailyTypesFromApi] = useState([]);
  const [receptionDailyFromApi, setReceptionDailyFromApi] = useState([]);
  const [washingDailyFromApi, setWashingDailyFromApi] = useState([]);
  const [receptionRejectionDailyFromApi, setReceptionRejectionDailyFromApi] = useState([]);
  const [workedHours, setWorkedHours] = useState({ horasTrabajadas: 0, horasAutorizadas: 0 });
  const [workedHoursDailyFromApi, setWorkedHoursDailyFromApi] = useState([]);
  const [workedHoursAuthorizedDailyFromApi, setWorkedHoursAuthorizedDailyFromApi] = useState([]);
  // Consumo de productos (litros) por producto
  const [productConsumption, setProductConsumption] = useState([]);
  const [pcLoading, setPcLoading] = useState(false);
  // Email modal state
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('Reporte General Pro-360');
  const [emailSending, setEmailSending] = useState(false);
  // Admin users for email selection
  const [adminUsers, setAdminUsers] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [selectedAdmins, setSelectedAdmins] = useState([]);
  // Días festivos (para resaltar en las gráficas diarias)
  const [holidaysList, setHolidaysList] = useState([]);
  // Participación por programa
  const [programasLavado, setProgramasLavado] = useState([]);
  const [kilosProcesados, setKilosProcesados] = useState(0);
  const [kilosFinalizados, setKilosFinalizados] = useState(0);
  const [loadingProg, setLoadingProg] = useState(false);
  // Productos estimados por programa de lavado (calculados desde productosUsados)
  const [productosPorPrograma, setProductosPorPrograma] = useState([]);
  // Indicador de planchado (alistamientos)
  const [alistamientoTotals, setAlistamientoTotals] = useState({
    totalAlistamientos: 0, totalPrendas: 0, totalKilos: 0,
    prendasLenceria: 0, totalKilosLenceria: 0, lenceriaAlistamientos: 0,
    prendasToallas: 0,  totalKilosToallas: 0,  toallasAlistamientos: 0,
  });
  const [alistamientosLoading, setAlistamientosLoading] = useState(false);
  const [alistamientoRawData, setAlistamientoRawData] = useState([]);

  // Toggle: servicios por kg (default ON)
  const [servicesPerKg, setServicesPerKg] = useState(true);
  // Toggle: eficiencia kg por hora autorizada (default ON)
  const [hoursEfficiencyPer, setHoursEfficiencyPer] = useState(true);
  // Toggles para la tarjeta compacta: visibilidad de series de horas
  const [showHoursWork, setShowHoursWork] = useState(false);
  const [showHoursAuth, setShowHoursAuth] = useState(false);
  const [showKgPerAuth, setShowKgPerAuth] = useState(true);
  const [showAlistKgPerAuth, setShowAlistKgPerAuth] = useState(true);
  // Horas: por defecto se cuentan todos los roles EXCEPTO conductor.
  // El usuario puede incluir conductores con este toggle.
  const [includeConductor, setIncludeConductor] = useState(false);

  const isCentroLavado = user?.role === R.CENTRO_LAVADO;

  // Refs eliminados: ahora están dentro de ReportsFilters

  // Cargar lavanderías si es admin u otros roles con acceso múltiple
  useEffect(() => {
    const load = async () => {
      try {
        // Con centros asignados el usuario elige entre ellos, aunque su rol
        // sea uno de los que normalmente quedan anclados a un solo centro.
        if ((isCentroLavado || user?.role === R.SUPERVISOR) && !tieneRestriccion) {
          // Para centro de lavado, cargar su propia lavandería para obtener indicadores
          if (user?.lavanderia?._id) {
            const resp = await fetchLaundries({ query: { _id: user.lavanderia._id }, limit: 1 });
            if (resp?.data?.[0]) {
              setSelectedLaundry(resp.data[0]);
            }
          }
          return;
        }
        setLaundries(await cargarCentros());
      } catch (e) {
        // silencioso
      }
    };
    load();
  }, [isCentroLavado, user?.lavanderia?._id, tieneRestriccion, cargarCentros]);

  // Cargar días festivos activos (una vez) para resaltarlos en las gráficas diarias
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await getActiveHolidays();
      if (!cancelled) setHolidaysList(Array.isArray(data) ? data : []);
    })();
    return () => { cancelled = true; };
  }, []);

  // Actualizar selectedLaundry cuando cambia idLavanderia (para admins)
  useEffect(() => {
    if (!idLavanderia || (isCentroLavado && !tieneRestriccion)) return;
    const laundry = laundries.find(l => l._id === idLavanderia);
    setSelectedLaundry(laundry || null);
  }, [idLavanderia, laundries, isCentroLavado]);

  // Normalizar rango de fechas
  const dateRange = useMemo(() => {
    const s = fromISODateOnly(startDate);
    const e = fromISODateOnly(endDate);
    const endOfDay = new Date(e);
    endOfDay.setHours(23, 59, 59, 999);
    return {
      $gte: new Date(s.getFullYear(), s.getMonth(), s.getDate()).toISOString(),
      $lte: endOfDay.toISOString(),
    };
  }, [startDate, endDate]);


  const canQuery = useMemo(
    () => !!idLavanderia && puedeVerCentro(idLavanderia),
    [idLavanderia, puedeVerCentro],
  );

  const loadData = useCallback(async () => {
    if (!canQuery) return;
    setLoading(true);
    try {
      const commonQuery = { 'lavanderia.id': idLavanderia, createdAt: dateRange };

      // 1) Recepciones (endpoints de reportes)
      try {
        setLoadingProg(true);
        const totalsResp = await getReceptionReportTotals({ query: commonQuery });
        const t = totalsResp?.data || {};
        setReceptionTotals({
          kilosProcesados: Number(t.kilosProcesados || 0),
          kilosRechazo: Number(t.kilosRechazo || 0),
          pendientes: Number(t.pendientes || 0),
          conteo: Number(t.conteo || 0),
          // incluir desglose de rechazo
          arrastre: Number(t.arrastre || 0),
          cloro: Number(t.cloro || 0),
          grasa: Number(t.grasa || 0),
          tintas: Number(t.tintas || 0),
          oxido: Number(t.oxido || 0),
          clorhexidina: Number(t.clorhexidina || 0),
          pegante: Number(t.pegante || 0),
          azul_de_metileno: Number(t.azul_de_metileno || 0),
          otro: Number(t.otro || 0),
          // opcional: kilosLimpios si se usa en otros lugares
          kilosLimpios: Number(t.kilosLimpios || 0),
          kilosFinalizados: Number(t?.kilosFinalizados || 0),
        });
        // Participación por programa
        const kp = Number(t.kilosProcesados || 0);
        const progs = Array.isArray(t.programasLavado) ? t.programasLavado : [];
        const mapped = progs
          .map(p => ({
            nombre: p?.nombre ?? 'Sin programa',
            kilos: Number(p?.kilos ?? 0),
            count: Number(p?.count ?? 0),
            peso_total_factor_Carga: Number(p?.peso_total_factor_Carga ?? 0),
          }))
          .sort((a, b) => b.kilos - a.kilos);
        setKilosProcesados(kp);
        setProgramasLavado(mapped);
        // Productos estimados por programa (viene ya agrupado desde el backend)
        const productosPorPrograma = Array.isArray(t.productosPorPrograma)
          ? t.productosPorPrograma
          : [];
        setProductosPorPrograma(productosPorPrograma);
      } catch (e) {
        setReceptionTotals({ kilosProcesados: 0, kilosRechazo: 0, pendientes: 0, conteo: 0, arrastre: 0, cloro: 0, grasa: 0, tintas: 0, oxido: 0, otro: 0, kilosLimpios: 0, kilosFinalizados: 0 });
        setKilosProcesados(0);
        setProgramasLavado([]);
        setProductosPorPrograma([]);
      } finally {
        setLoadingProg(false);
      }

      try {
        const dailyResp = await getReceptionDaily({ query: commonQuery, groupBy: 'day' });
        const items = Array.isArray(dailyResp?.data) ? dailyResp.data : [];
        // Series separadas: procesados (pesoKg) y rechazo (rechazoKg)
        const processed = items.map(it => ({
          date: String(it.date || '').slice(0, 10),
          value: Number(it.pesoKg ?? 0),
        }));
        const rejected = items.map(it => ({
          date: String(it.date || '').slice(0, 10),
          value: Number(it.rechazoKg ?? 0),
        }));
        setReceptionDailyFromApi(processed);
        setReceptionRejectionDailyFromApi(rejected);
      } catch (e) {
        setReceptionDailyFromApi([]); // se usará fallback local
        setReceptionRejectionDailyFromApi([]);
      }

      // 2) Ciclos de lavado (endpoints de reportes)
      try {
        const wTotalsResp = await getWashingCycleReportTotals({ query: commonQuery });
        const wt = wTotalsResp?.data || {};
        setWashingTotals({
          kilosLavados: Number(wt.kilosLavados || wt.kilos || 0),
          ciclos: Number(wt.ciclos || 0),
        });
        // Ciclos abortados reales (filtrando abortado: true)
        try {
          const abortedResp = await fetchWashingCycles({ limit: 10000, page: 1, query: { 'lavanderia.id': idLavanderia, fecha: dateRange, abortado: true } });
          const abortedItems = Array.isArray(abortedResp?.data) ? abortedResp.data : [];
          const totalAbortedCycles = abortedItems.reduce((sum, it) => sum + Number(it.ciclos || 0), 0);
          const totalAbortedKilos = abortedItems.reduce((sum, it) => sum + (Number(it.ciclos || 0) * Number(it.capacidad || 0)), 0);
          setWashingAbortedTotals(totalAbortedCycles);
          setWashingAbortedKilos(totalAbortedKilos);
        } catch (e) {
          setWashingAbortedTotals(0);
          setWashingAbortedKilos(0);
        }
        // Desglose por máquina (servicio dedicado)
        try {
          const partResp = await getWashingParticipationByMachine({ query: { 'lavanderia.id': idLavanderia, fecha: dateRange } });
          const arr = Array.isArray(partResp?.data) ? partResp.data : [];
          const arrAborted = Array.isArray(partResp?.aborted) ? partResp.aborted : [];
          setWashingByMachine(arr);
          setWashingAbortedByMachine(arrAborted);
        } catch (e) {
          setWashingByMachine([]);
          setWashingAbortedByMachine([]);
        }
      } catch (e) {
        setWashingTotals({ kilosLavados: 0, ciclos: 0 });
        setWashingAbortedTotals(0);
        setWashingAbortedKilos(0);
        setWashingByMachine([]);
        setWashingAbortedByMachine([]);
      }

      // 2b) Ciclos de secado
      try {
        const dTotalsResp = await getDryingCycleReportTotals({ query: commonQuery });
        const dt = dTotalsResp?.data || {};
        setDryingTotals({
          kilosSecados: Number(dt.kilosSecados || dt.kilos || 0),
          ciclos: Number(dt.ciclos || 0),
        });
        // Ciclos abortados de secado reales
        try {
          const abortedResp = await fetchDryingCycles({ limit: 10000, page: 1, query: { 'lavanderia.id': idLavanderia, fecha: dateRange, abortado: true } });
          const abortedItems = Array.isArray(abortedResp?.data) ? abortedResp.data : [];
          const totalAbortedCycles = abortedItems.reduce((sum, it) => sum + Number(it.ciclos || 0), 0);
          const totalAbortedKilos = abortedItems.reduce((sum, it) => sum + (Number(it.ciclos || 0) * Number(it.capacidad || 0)), 0);
          setDryingAbortedTotals(totalAbortedCycles);
          setDryingAbortedKilos(totalAbortedKilos);
        } catch (e) {
          setDryingAbortedTotals(0);
          setDryingAbortedKilos(0);
        }
      } catch (e) {
        setDryingTotals({ kilosSecados: 0, ciclos: 0 });
        setDryingAbortedTotals(0);
        setDryingAbortedKilos(0);
      }

      // Serie diaria de ciclos desde backend (kg lavados por día)
      try {
        const wDailyResp = await getWashingCycleDaily({ query: commonQuery, groupBy: 'day' });
        const items = Array.isArray(wDailyResp?.data) ? wDailyResp.data : [];
        const normalized = items.map(it => ({
          date: String(it.date || '').slice(0, 10),
          value: Number(it.kilosLavados ?? it.kilos ?? 0),
        }));
        setWashingDailyFromApi(normalized);
      } catch (e) {
        setWashingDailyFromApi([]);
      }

      // 3) Servicios (agua/energía/gas): totales y serie diaria
      try {
        const uTotalsResp = await getUtilityReportTotals({ query: commonQuery });
        const data = uTotalsResp?.data || {};
        const tot = data?.totales || {};
        const porTipo = Array.isArray(data?.porTipo) ? data.porTipo : [];
        setUtilityTotals({
          consumoLitros: Number(tot.consumoLitros || 0),
          costoTotal: Number(tot.costos || tot.costoTotal || 0),
        });
        const byType = { agua: { consumoLitros: 0, costos: 0 }, gas: { consumoLitros: 0, costos: 0 }, Electricidad: { consumoLitros: 0, costos: 0 }, aguaCaliente: { consumoLitros: 0, costos: 0 } };
        for (const item of porTipo) {
          const raw = String(item?.tipo || '').trim();
          const low = raw.toLowerCase();
          let k = '';
          if (low === 'agua') k = 'agua';
          else if (low === 'gas') k = 'gas';
          else if (low === 'electricidad') k = 'Electricidad';
          else if (low === 'agua caliente') k = 'aguaCaliente';
          // fallback por si llegan variantes inesperadas
          else if (/agua/i.test(raw)) k = 'agua';
          else if (/gas/i.test(raw)) k = 'gas';
          else if (/elec/i.test(raw)) k = 'Electricidad';
          else if (/agua caliente/i.test(raw)) k = 'aguaCaliente';
          if (k && Object.prototype.hasOwnProperty.call(byType, k)) {
            byType[k] = {
              consumoLitros: Number(item.consumoLitros || 0),
              costos: Number(item.costos || 0),
            };
          }
        }
        setUtilityTypeTotals(byType);
      } catch (e) {
        setUtilityTotals({ consumoLitros: 0, costoTotal: 0 });
        setUtilityTypeTotals({ agua: { consumoLitros: 0, costos: 0 }, gas: { consumoLitros: 0, costos: 0 }, Electricidad: { consumoLitros: 0, costos: 0 }, aguaCaliente: { consumoLitros: 0, costos: 0 } });
      }

      try {
        const uDailyResp = await getUtilityDaily({ query: commonQuery, groupBy: 'day' });
        const items = Array.isArray(uDailyResp?.data) ? uDailyResp.data : [];
        // normalizar a formato {date, value} usando total.consumoLitros
        const normalized = items.map(it => ({
          date: String(it.date || '').slice(0, 10),
          value: Number(it?.total?.consumoLitros || 0),
        }));
        setUtilityDailyFromApi(normalized);
        // guardar por tipo para futuras gráficas
        const byTypeSeries = items.map(it => ({
          date: String(it.date || '').slice(0, 10),
          agua: {
            consumoLitros: Number(it?.agua?.consumoLitros || 0),
            costos: Number(it?.agua?.costos || 0),
          },
          gas: {
            consumoLitros: Number(it?.gas?.consumoLitros || 0),
            costos: Number(it?.gas?.costos || 0),
          },
          Electricidad: {
            consumoLitros: Number(it?.Electricidad?.consumoLitros || 0),
            costos: Number(it?.Electricidad?.costos || 0),
          },
          aguaCaliente: {
            consumoLitros: Number(it?.aguaCaliente?.consumoLitros || 0),
            costos: Number(it?.aguaCaliente?.costos || 0),
          },
          total: {
            consumoLitros: Number(it?.total?.consumoLitros || 0),
            costos: Number(it?.total?.costos || 0),
          },
        }));
        setUtilityDailyTypesFromApi(byTypeSeries);
      } catch (e) {
        setUtilityDailyFromApi([]);
        setUtilityDailyTypesFromApi([]);
      }

      // 4) Horas trabajadas: totales y serie diaria desde backend (usar campo fecha)
      try {
        // Por defecto excluye conductores; si includeConductor, cuenta todos los roles.
        // Forma JSON-serializable ($regex string, no RegExp) para que viaje en el body.
        const rolFilter = includeConductor ? {} : { 'empleado.rol': { $not: { $regex: 'conductor', $options: 'i' } } };
        const whQuery = { 'lavanderia.id': idLavanderia, fecha: dateRange, ...rolFilter };
        // Totales
        const whTotalsResp = await getWorkedHoursSummary(whQuery);
        const wht = whTotalsResp?.data || {};
        setWorkedHours({
          horasTrabajadas: Number(wht.totalHorasTrabajadas || 0),
          horasAutorizadas: Number(wht.totalHorasAutorizadas || 0),
        });
        // Serie diaria
        const whDailyResp = await getWorkedHoursDailyReport({ query: whQuery, groupBy: 'day' });
        const items = Array.isArray(whDailyResp?.data) ? whDailyResp.data : [];
        const normalized = items.map(it => ({
          date: String(it.date || '').slice(0, 10),
          value: Number(it.totalHorasTrabajadas || 0),
        }));
        setWorkedHoursDailyFromApi(normalized);
        const normalizedAuth = items.map(it => ({
          date: String(it.date || '').slice(0, 10),
          value: Number(it.totalHorasAutorizadas || 0),
        }));
        setWorkedHoursAuthorizedDailyFromApi(normalizedAuth);
      } catch (e) {
        setWorkedHours({ horasTrabajadas: 0, horasAutorizadas: 0 });
        setWorkedHoursDailyFromApi([]);
        setWorkedHoursAuthorizedDailyFromApi([]);
      }

      // 5) Consumo de productos (litros) agrupado por producto
      try {
        setPcLoading(true);
        const pcResp = await getProductConsumptionTotals({ lavanderiaId: idLavanderia, from: dateRange.$gte, to: dateRange.$lte });
        const arr = Array.isArray(pcResp?.data) ? pcResp.data : [];
        setProductConsumption(arr);
      } catch (e) {
        setProductConsumption([]);
      } finally {
        setPcLoading(false);
      }
      // 6) Alistamientos — Indicador de planchado (Lenceria + Toallas)
      try {
        setAlistamientosLoading(true);
        const alistResp = await getAlistamientosByLavanderia(idLavanderia, {
          query: {
            estado: { $nin: ['Pendiente', 'Eliminado'] },
            fechaCreacion: dateRange,
          },
          page: 1,
          limit: 10000,
        });
        const alistData = alistResp.data || [];
        setAlistamientoRawData(alistData);
        const lenceria = alistData.filter(a => a.zona === 'Lenceria');
        const toallas  = alistData.filter(a => a.zona === 'Toallas');
        setAlistamientoTotals({
          totalAlistamientos:   alistData.length,
          totalPrendas:         alistData.reduce((s, a) => s + (a.totalItems || 0), 0),
          totalKilos:           parseFloat((alistData.reduce((s, a) => s + (a.pesoTotal || 0), 0) / 1000).toFixed(2)),
          prendasLenceria:      lenceria.reduce((s, a) => s + (a.totalItems || 0), 0),
          totalKilosLenceria:   parseFloat((lenceria.reduce((s, a) => s + (a.pesoTotal || 0), 0) / 1000).toFixed(2)),
          lenceriaAlistamientos: lenceria.length,
          prendasToallas:       toallas.reduce((s, a) => s + (a.totalItems || 0), 0),
          totalKilosToallas:    parseFloat((toallas.reduce((s, a) => s + (a.pesoTotal || 0), 0) / 1000).toFixed(2)),
          toallasAlistamientos:  toallas.length,
        });
      } catch (e) {
        setAlistamientoRawData([]);
        // silencioso: el indicador simplemente queda en 0
      } finally {
        setAlistamientosLoading(false);
      }

    } catch (e) {
      toast({ title: 'Reportes', description: e.message || 'Error al cargar reportes', variant: 'destructive' });
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  }, [canQuery, idLavanderia, dateRange, toast, includeConductor]);

  // Recargar cuando cambia la inclusión de conductores (si ya se consultó).
  useEffect(() => {
    if (hasLoaded && canQuery) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeConductor]);

  useEffect(() => {
    // fijar lavandería si rol centro_lavado (salvo que tenga centros asignados,
    // en cuyo caso elige entre ellos con el selector)
    if (isCentroLavado && !tieneRestriccion) {
      const myId = user?.lavanderia?._id || user?.lavanderia?.id;
      if (myId) setIdLavanderia(String(myId));
    }
  }, [isCentroLavado, tieneRestriccion, user]);

  // Cargar usuarios admin cuando se abre el modal
  useEffect(() => {
    if (emailModalOpen && adminUsers.length === 0) {
      loadAdminUsers();
    }
  }, [emailModalOpen]);

  const loadAdminUsers = async () => {
    try {
      setLoadingAdmins(true);
      const response = await fetchAdminUsers({ estado: 'Activo' });
      setAdminUsers(response?.data || []);
    } catch (error) {
      console.error('Error al cargar usuarios admin:', error);
      toast({ title: 'Error', description: 'No se pudieron cargar los usuarios admin', variant: 'destructive' });
    } finally {
      setLoadingAdmins(false);
    }
  };

  const toggleAdminSelection = (admin) => {
    setSelectedAdmins(prev => {
      const isSelected = prev.some(a => a._id === admin._id);
      if (isSelected) {
        return prev.filter(a => a._id !== admin._id);
      } else {
        return [...prev, admin];
      }
    });
  };

  const selectAllAdmins = () => {
    setSelectedAdmins([...adminUsers]);
  };

  const clearAdminSelection = () => {
    setSelectedAdmins([]);
  };

  // Genera el HTML del reporte usando el componente separado
  const buildReportHtml = useCallback(() => {
    const laundryName = (() => {
      const it = (laundries || []).find(l => String(l?._id || l?.id) === String(idLavanderia));
      return it?.nombre || '';
    })();

    return EmailReportBuilder.buildReportHtml({
      laundryName,
      startDate,
      endDate,
      receptionTotals,
      washingTotals,
      utilityTotals,
      utilityTypeTotals,
      workedHours,
      productConsumption,
      programasLavado,
      kilosProcesados
    });
  }, [laundries, idLavanderia, startDate, endDate, receptionTotals, washingTotals, utilityTotals, utilityTypeTotals, workedHours, productConsumption, programasLavado, kilosProcesados]);

  const handleSendEmail = useCallback(async () => {
    try {
      setEmailSending(true);

      // Determinar destinatarios: manual o seleccionados
      const recipients = selectedAdmins.length > 0
        ? selectedAdmins.map(admin => admin.correo).join(', ')
        : emailTo;

      if (!recipients) {
        toast({ title: 'Error', description: 'Debe especificar al menos un destinatario', variant: 'destructive' });
        return;
      }

      // Preparar datos del reporte para el backend
      const reportData = {
        laundryName: laundries.find(l => l._id === idLavanderia)?.nombre || 'Lavandería',
        startDate,
        endDate,
        receptionTotals,
        washingTotals,
        utilityTotals,
        utilityTypeTotals,
        workedHours,
        productConsumption,
        programasLavado,
        kilosProcesados
      };

      // Usar el nuevo endpoint que genera HTML en el backend
      await sendReportByData({ to: recipients, subject: emailSubject, reportData });

      const count = selectedAdmins.length > 0 ? selectedAdmins.length : 1;
      toast({ title: 'Éxito', description: `Reporte enviado a ${count} destinatario${count > 1 ? 's' : ''}` });
      setEmailModalOpen(false);
      setEmailTo('');
      setSelectedAdmins([]);
    } catch (error) {
      console.error('Error enviando correo:', error);
      toast({ title: 'Error', description: error.message || 'Error al enviar el correo.', variant: 'destructive' });
    } finally {
      setEmailSending(false);
    }
  }, [laundries, idLavanderia, startDate, endDate, receptionTotals, washingTotals, utilityTotals, utilityTypeTotals, workedHours, productConsumption, programasLavado, kilosProcesados, selectedAdmins, emailTo, emailSubject, toast]);

  // Datos para gráfica de barras simple (sin dependencias externas)
  const chartData = useMemo(() => ([
    { key: 'recepcion', label: 'Recepción (Kg)', value: Number(receptionTotals.kilosProcesados || 0), color: '#10b981' },
    { key: 'ciclos', label: 'Ciclos (Kg lavados)', value: Number(washingTotals.kilosLavados || 0), color: '#6366f1' },
    { key: 'agua', label: 'Agua (L)', value: Number(utilityTypeTotals.agua.consumoLitros || 0), color: '#0ea5e9' },
    { key: 'aguaCaliente', label: 'Agua caliente (L)', value: Number(utilityTypeTotals.aguaCaliente?.consumoLitros || 0), color: '#0ea5e9' },
    { key: 'gas', label: 'Gas (m<sup>3</sup>)', value: Number(utilityTypeTotals.gas.consumoLitros || 0), color: '#f59e0b' },
    { key: 'electricidad', label: 'Electricidad (L)', value: Number(utilityTypeTotals.Electricidad.consumoLitros || 0), color: '#a21caf' },
    { key: 'horas', label: 'Horas trabajadas', value: Number(workedHours.horasTrabajadas || 0), color: '#6b7280' },
  ]), [utilityTypeTotals, receptionTotals.kilosProcesados, washingTotals.kilosLavados, workedHours.horasTrabajadas]);

  // Máximo combinado para escalar barras comparables
  const maxChartValue = useMemo(() => {
    const m = Math.max(...chartData.map(d => d.value));
    return m > 0 ? m : 1; // evitar división por 0
  }, [chartData]);

  // Utilidad: consumo por día
  const toYMD = (d) => {
    const dt = new Date(d);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const day = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const daysInRange = useMemo(() => {
    const start = fromISODateOnly(startDate);
    const end = fromISODateOnly(endDate);
    // normalizar a medianoche local
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    const days = [];
    const cursor = new Date(start);
    while (cursor <= end) {
      days.push(toYMD(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return days;
  }, [startDate, endDate]);

  // Mapa 'YYYY-MM-DD' (día local) -> nombre del festivo, limitado al rango visible.
  // Se usa la fecha local (igual que EmployeePayrollPage) para alinear con daysInRange.
  const holidaysMap = useMemo(() => {
    const map = {};
    const days = new Set(daysInRange);
    for (const h of holidaysList) {
      if (!h?.fecha) continue;
      const key = toYMD(new Date(h.fecha));
      if (days.has(key)) map[key] = h?.nombre || 'Festivo';
    }
    return map;
  }, [holidaysList, daysInRange]);

  const dailyConsumption = useMemo(() => {
    const map = new Map();
    for (const d of daysInRange) map.set(d, 0);
    if (utilityDailyFromApi?.length) {
      for (const it of utilityDailyFromApi) {
        const key = typeof it.date === 'string' ? String(it.date).slice(0, 10) : toYMD(it.date || new Date());
        const val = Number(it.value || 0);
        if (map.has(key)) map.set(key, map.get(key) + val);
      }
    }
    return daysInRange.map((d) => ({ date: d, value: map.get(d) || 0 }));
  }, [utilityDailyFromApi, daysInRange]);

  // Serie diaria por tipo (para futuras gráficas), con fallback a lista cuando no hay API
  const dailyUtilitiesByType = useMemo(() => {
    const map = new Map(daysInRange.map(d => [d, { agua: 0, gas: 0, Electricidad: 0 }]));
    if (utilityDailyTypesFromApi?.length) {
      for (const it of utilityDailyTypesFromApi) {
        const key = toYMD(it.date || new Date());
        if (map.has(key)) {
          const cur = map.get(key);
          cur.agua += Number(it.agua || 0);
          cur.gas += Number(it.gas || 0);
          cur.Electricidad += Number(it.Electricidad || 0);
          map.set(key, cur);
        }
      }
    }
    return daysInRange.map(d => ({ date: d, ...map.get(d) }));
  }, [utilityDailyTypesFromApi, daysInRange]);

  const maxDaily = useMemo(() => {
    const m = Math.max(...dailyConsumption.map(d => d.value));
    return m > 0 ? m : 1;
  }, [dailyConsumption]);

  // Fallback: datos de ejemplo si no hay consumo real (todos 0)
  const isDailyEmpty = useMemo(() => dailyConsumption.every(d => d.value === 0), [dailyConsumption]);
  const mockDaily = useMemo(() => {
    if (!isDailyEmpty) return [];
    return daysInRange.map((d, i) => ({
      date: d,
      value: Math.max(0, Math.round(80 + 60 * Math.sin(i / 2) + Math.random() * 40)),
    }));
  }, [isDailyEmpty, daysInRange]);
  const dailySeries = isDailyEmpty ? mockDaily : dailyConsumption;
  const maxDailySeries = useMemo(() => {
    const m = Math.max(...dailySeries.map(d => d.value));
    return m > 0 ? m : 1;
  }, [dailySeries]);

  // Recepción (kg) por día usando backend si está disponible; fallback a cálculo local
  const dailyReception = useMemo(() => {
    const map = new Map();
    for (const d of daysInRange) map.set(d, 0);
    for (const it of receptionDailyFromApi) {
      const key = typeof it.date === 'string' ? String(it.date).slice(0, 10) : toYMD(it.date || new Date());
      const val = Number(it.value || 0);
      if (map.has(key)) map.set(key, map.get(key) + val);
    }
    return daysInRange.map((d) => ({ date: d, value: map.get(d) || 0 }));
  }, [receptionDailyFromApi, daysInRange]);

  // Ciclos (kg lavados) por día — usar backend si está disponible; fallback a lista local
  const dailyCycles = useMemo(() => {
    const map = new Map();
    for (const d of daysInRange) map.set(d, 0);
    for (const it of washingDailyFromApi) {
      const key = typeof it.date === 'string' ? String(it.date).slice(0, 10) : toYMD(it.date || new Date());
      const val = Number(it.value || 0);
      if (map.has(key)) map.set(key, map.get(key) + val);
    }
    return daysInRange.map((d) => ({ date: d, value: map.get(d) || 0 }));
  }, [washingDailyFromApi, daysInRange]);

  // Rechazo (kg) por día desde recepciones
  const dailyRejection = useMemo(() => {
    const map = new Map();
    for (const d of daysInRange) map.set(d, 0);
    for (const it of receptionRejectionDailyFromApi) {
      const key = typeof it.date === 'string' ? String(it.date).slice(0, 10) : toYMD(it.date || new Date());
      const val = Number(it.value || 0);
      if (map.has(key)) map.set(key, map.get(key) + val);
    }
    return daysInRange.map((d) => ({ date: d, value: map.get(d) || 0 }));
  }, [receptionRejectionDailyFromApi, daysInRange]);

  // Horas trabajadas por día desde backend; si todo 0, usar ejemplo para visual
  const dailyHours = useMemo(() => {
    const map = new Map();
    for (const d of daysInRange) map.set(d, 0);
    for (const it of workedHoursDailyFromApi) {
      const key = typeof it.date === 'string' ? String(it.date).slice(0, 10) : toYMD(it.date || new Date());
      const val = Number(it.value || 0);
      if (map.has(key)) map.set(key, map.get(key) + val);
    }
    return daysInRange.map((d) => ({ date: d, value: map.get(d) || 0 }));
  }, [workedHoursDailyFromApi, daysInRange]);
  // Mostrar siempre las horas reales (antes: si venían en 0 se inventaban datos
  // aleatorios de ejemplo, lo que no coincidía con los datos reales).
  const dailyHoursSeries = dailyHours;

  // Horas autorizadas por día (serie adicional)
  const dailyHoursAuthorized = useMemo(() => {
    const map = new Map();
    for (const d of daysInRange) map.set(d, 0);
    for (const it of workedHoursAuthorizedDailyFromApi) {
      const key = typeof it.date === 'string' ? String(it.date).slice(0, 10) : toYMD(it.date || new Date());
      const val = Number(it.value || 0);
      if (map.has(key)) map.set(key, map.get(key) + val);
    }
    return daysInRange.map((d) => ({ date: d, value: map.get(d) || 0 }));
  }, [workedHoursAuthorizedDailyFromApi, daysInRange]);

  // Horas autorizadas por kilo de recepción (h/kg) por día
  const dailyHoursAuthorizedPerKg = useMemo(() => {
    if (!daysInRange?.length) return [];
    return daysInRange.map((d, idx) => {
      const num = Number(dailyHoursAuthorized?.[idx]?.value || 0);
      const den = Number(dailyReception?.[idx]?.value || 0);
      const v = den > 0 ? num / den : 0;
      return { date: d, value: v };
    });
  }, [daysInRange, dailyHoursAuthorized, dailyReception]);

  // Kg por hora autorizada (kg/h) por día — Lavado
  const dailyKgPerAuthHour = useMemo(() => {
    if (!daysInRange?.length) return [];
    return daysInRange.map((d, idx) => {
      const rec = Number(dailyReception?.[idx]?.value || 0);
      const hrsAuth = Number(dailyHoursAuthorized?.[idx]?.value || 0);
      const v = hrsAuth > 0 ? rec / hrsAuth : 0;
      return { date: d, value: v };
    });
  }, [daysInRange, dailyReception, dailyHoursAuthorized]);

  // Meta de eficiencia (kg/h autorizada) del centro y media del período.
  // La media = ratio global (Σ kg procesados / Σ horas autorizadas), igual que
  // el indicador "Mano de Obra" de la tabla Indicadores de Gestión por Kg.
  const kgPerAuthMeta = Number(selectedLaundry?.indicadores?.manoDeObra?.consumoEsperado || 0);
  const kgPerAuthMedia = useMemo(() => {
    const kg = Number(kilosProcesados || 0);
    const hrs = Number(workedHours?.horasAutorizadas || 0);
    return hrs > 0 ? kg / hrs : 0;
  }, [kilosProcesados, workedHours]);
  // Estadísticos diarios (para mostrar debajo de la gráfica de eficiencia).
  const kgPerAuthStats = useMemo(() => {
    const vals = (dailyKgPerAuthHour || []).map((d) => Number(d.value || 0)).filter((v) => v > 0);
    const dailyAvg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
    const max = vals.length ? Math.max(...vals) : 0;
    const min = vals.length ? Math.min(...vals) : 0;
    const arr = dailyKgPerAuthHour || [];
    const last = arr.length ? Number(arr[arr.length - 1]?.value || 0) : 0;
    return { dailyAvg, max, min, last, activeDays: vals.length };
  }, [dailyKgPerAuthHour]);

  // Kg alistados por hora autorizada (kg/h) por día — Planchado
  const dailyAlistKgPerAuthHour = useMemo(() => {
    if (!daysInRange?.length) return [];
    const kgByDay = new Map();
    for (const a of alistamientoRawData) {
      const d = String(a.fechaCreacion || '').slice(0, 10);
      kgByDay.set(d, (kgByDay.get(d) || 0) + (a.pesoTotal || 0));
    }
    return daysInRange.map((d, idx) => {
      const kg = (kgByDay.get(d) || 0) / 1000;
      const hrsAuth = Number(dailyHoursAuthorized?.[idx]?.value || 0);
      return { date: d, value: hrsAuth > 0 ? kg / hrsAuth : 0 };
    });
  }, [daysInRange, alistamientoRawData, dailyHoursAuthorized]);

  // Servicios ya calculado: dailySeries

  // Series diarias por tipo de servicio (agua, gas, electricidad)
  const dailyWaterSeries = useMemo(() => {
    const map = new Map();
    for (const d of daysInRange) map.set(d, 0);
    for (const it of utilityDailyTypesFromApi) {
      const key = String(it.date || '').slice(0, 10);
      const val = Number(it?.agua?.consumoLitros || 0);
      if (map.has(key)) map.set(key, map.get(key) + val);
    }
    return daysInRange.map((d) => ({ date: d, value: map.get(d) || 0 }));
  }, [utilityDailyTypesFromApi, daysInRange]);

  const dailyGasSeries = useMemo(() => {
    const map = new Map();
    for (const d of daysInRange) map.set(d, 0);
    for (const it of utilityDailyTypesFromApi) {
      const key = String(it.date || '').slice(0, 10);
      const val = Number(it?.gas?.consumoLitros || 0);
      if (map.has(key)) map.set(key, map.get(key) + val);
    }
    return daysInRange.map((d) => ({ date: d, value: map.get(d) || 0 }));
  }, [utilityDailyTypesFromApi, daysInRange]);

  const dailyElectricSeries = useMemo(() => {
    const map = new Map();
    for (const d of daysInRange) map.set(d, 0);
    for (const it of utilityDailyTypesFromApi) {
      const key = String(it.date || '').slice(0, 10);
      const val = Number(it?.Electricidad?.consumoLitros || 0);
      if (map.has(key)) map.set(key, map.get(key) + val);
    }
    return daysInRange.map((d) => ({ date: d, value: map.get(d) || 0 }));
  }, [utilityDailyTypesFromApi, daysInRange]);

  const dailyHotWaterSeries = useMemo(() => {
    const map = new Map();
    for (const d of daysInRange) map.set(d, 0);
    for (const it of utilityDailyTypesFromApi) {
      const key = String(it.date || '').slice(0, 10);
      const val = Number(it?.aguaCaliente?.consumoLitros || 0);
      if (map.has(key)) map.set(key, map.get(key) + val);
    }
    return daysInRange.map((d) => ({ date: d, value: map.get(d) || 0 }));
  }, [utilityDailyTypesFromApi, daysInRange]);

  // Series por kilo: servicios divididos entre recepción (kg) por día
  const dailyWaterPerKg = useMemo(() => {
    if (!daysInRange?.length) return [];
    return daysInRange.map((d, idx) => {
      const num = Number(dailyWaterSeries?.[idx]?.value || 0);
      const den = Number(dailyReception?.[idx]?.value || 0);
      const v = den > 0 ? num / den : 0;
      return { date: d, value: v };
    });
  }, [daysInRange, dailyWaterSeries, dailyReception]);

  const dailyGasPerKg = useMemo(() => {
    if (!daysInRange?.length) return [];
    return daysInRange.map((d, idx) => {
      const num = Number(dailyGasSeries?.[idx]?.value || 0);
      const den = Number(dailyReception?.[idx]?.value || 0);
      const v = den > 0 ? num / den : 0;
      return { date: d, value: v };
    });
  }, [daysInRange, dailyGasSeries, dailyReception]);

  const dailyElectricPerKg = useMemo(() => {
    if (!daysInRange?.length) return [];
    return daysInRange.map((d, idx) => {
      const num = Number(dailyElectricSeries?.[idx]?.value || 0);
      const den = Number(dailyReception?.[idx]?.value || 0);
      const v = den > 0 ? num / den : 0;
      return { date: d, value: v };
    });
  }, [daysInRange, dailyElectricSeries, dailyReception]);

  const dailyHotWaterPerKg = useMemo(() => {
    if (!daysInRange?.length) return [];
    return daysInRange.map((d, idx) => {
      const num = Number(dailyHotWaterSeries?.[idx]?.value || 0);
      const den = Number(dailyReception?.[idx]?.value || 0);
      const v = den > 0 ? num / den : 0;
      return { date: d, value: v };
    });
  }, [daysInRange, dailyHotWaterSeries, dailyReception]);

  // Series para la sección Recepción y Servicios (modo dinámico)
  const servicesSeriesAbs = useMemo(() => ([
    { key: 'rec', label: 'Recepción (Kg)', color: '#10b981', data: dailyReception },
    { key: 'agua', label: 'Agua (L)', color: '#0ea5e9', data: dailyWaterSeries, scale: 1 },
    { key: 'aguaCal', label: 'Agua caliente (L)', color: '#f97316', data: dailyHotWaterSeries, scale: 1 },
    { key: 'gas', label: 'Gas (m3)', color: '#f59e0b', data: dailyGasSeries, scale: 1 },
    { key: 'elec', label: 'Electricidad (kWh)', color: '#a21caf', data: dailyElectricSeries, scale: 1 },
  ]), [dailyReception, dailyWaterSeries, dailyHotWaterSeries, dailyGasSeries, dailyElectricSeries]);

  const servicesSeriesPerKg = useMemo(() => ([
    { key: 'aguaKg', label: 'Agua (L/kg)', color: '#0ea5e9', data: dailyWaterPerKg, scale: 1 },
    { key: 'aguaCalKg', label: 'Agua caliente (L/kg)', color: '#f97316', data: dailyHotWaterPerKg, scale: 1 },
    { key: 'gasKg', label: 'Gas (m3/kg)', color: '#f59e0b', data: dailyGasPerKg, scale: 23 },
    { key: 'elecKg', label: 'Electricidad (kWh/kg)', color: '#a21caf', data: dailyElectricPerKg, scale: 7 },
  ]), [dailyWaterPerKg, dailyHotWaterPerKg, dailyGasPerKg, dailyElectricPerKg]);

  // Series de costos por tipo (agua, gas, electricidad)
  const dailyWaterCostSeries = useMemo(() => {
    const map = new Map();
    for (const d of daysInRange) map.set(d, 0);
    for (const it of utilityDailyTypesFromApi) {
      const key = String(it.date || '').slice(0, 10);
      const val = Number(it?.agua?.costos || 0);
      if (map.has(key)) map.set(key, map.get(key) + val);
    }
    return daysInRange.map((d) => ({ date: d, value: map.get(d) || 0 }));
  }, [utilityDailyTypesFromApi, daysInRange]);

  const dailyGasCostSeries = useMemo(() => {
    const map = new Map();
    for (const d of daysInRange) map.set(d, 0);
    for (const it of utilityDailyTypesFromApi) {
      const key = String(it.date || '').slice(0, 10);
      const val = Number(it?.gas?.costos || 0);
      if (map.has(key)) map.set(key, map.get(key) + val);
    }
    return daysInRange.map((d) => ({ date: d, value: map.get(d) || 0 }));
  }, [utilityDailyTypesFromApi, daysInRange]);

  const dailyElectricCostSeries = useMemo(() => {
    const map = new Map();
    for (const d of daysInRange) map.set(d, 0);
    for (const it of utilityDailyTypesFromApi) {
      const key = String(it.date || '').slice(0, 10);
      const val = Number(it?.Electricidad?.costos || 0);
      if (map.has(key)) map.set(key, map.get(key) + val);
    }
    return daysInRange.map((d) => ({ date: d, value: map.get(d) || 0 }));
  }, [utilityDailyTypesFromApi, daysInRange]);

  const dailyHotWaterCostSeries = useMemo(() => {
    const map = new Map();
    for (const d of daysInRange) map.set(d, 0);
    for (const it of utilityDailyTypesFromApi) {
      const key = String(it.date || '').slice(0, 10);
      const val = Number(it?.aguaCaliente?.costos || 0);
      if (map.has(key)) map.set(key, map.get(key) + val);
    }
    return daysInRange.map((d) => ({ date: d, value: map.get(d) || 0 }));
  }, [utilityDailyTypesFromApi, daysInRange]);

  // Máximo combinado para escalar barras comparables
  const combinedMax = useMemo(() => {
    const all = [
      ...dailySeries.map(d => d.value),
      ...dailyReception.map(d => d.value),
      ...dailyCycles.map(d => d.value),
      ...dailyHoursSeries.map(d => d.value),
    ];
    const m = Math.max(...all);
    return m > 0 ? m : 1;
  }, [dailySeries, dailyReception, dailyCycles, dailyHoursSeries]);

  // Porcentaje de rechazo vs recepción por día
  const dailyRejectionPct = useMemo(() => {
    if (!daysInRange?.length) return [];
    // Aseguramos alineación por índice con daysInRange
    return daysInRange.map((d, idx) => {
      const rec = Number(dailyReception[idx]?.value || 0);
      const rej = Number(dailyRejection[idx]?.value || 0);
      const pct = rec > 0 ? (rej / rec) * 100 : 0;
      return { date: d, value: Number.isFinite(pct) ? pct : 0 };
    });
  }, [daysInRange, dailyReception, dailyRejection]);

  // (Opcional) Máximo por tipo de servicios - no usado actualmente

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 md:p-6 lg:p-8"
    >
      {/* Header Section */}
      <div className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200/80 dark:border-gray-700/80 p-6 md:p-8 mb-8">
        {/* Gradient accent bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />
        <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-md shadow-indigo-200 dark:shadow-indigo-900/40">
              <BarChart3 className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent leading-tight">Reporte General Pro-360</h1>
            </div>
          </div>
     
            <ReportsFilters
              isCentroLavado={isCentroLavado && !tieneRestriccion}
              laundries={laundries}
              idLavanderia={idLavanderia}
              setIdLavanderia={setIdLavanderia}
              startDate={startDate}
              endDate={endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              loading={loading}
              canQuery={canQuery}
              onApply={loadData}
            />
            {/* Botón enviar por correo */}
{/*             <Button
              type="button"
              variant="outline"
              disabled={!canQuery}
              onClick={() => setEmailModalOpen(true)}
              className="h-10 gap-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 hover:border-indigo-300 dark:hover:border-indigo-500 transition-colors whitespace-nowrap"
            >
              <Mail className="h-4 w-4" />
              Enviar por correo
            </Button> 
          </div>*/}
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-32">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 blur-xl opacity-20 animate-pulse" />
            <Loader2 className="h-12 w-12 text-blue-600 dark:text-blue-400 animate-spin relative" />
          </div>
          <p className="mt-6 text-lg font-semibold text-gray-700 dark:text-gray-300">Cargando reportes...</p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Esto puede tomar unos segundos</p>
        </div>
      )}

      {/* Empty / Initial State */}
      {!loading && !hasLoaded && (
        <div className="flex flex-col items-center justify-center py-32 text-center">
          <div className="flex items-center justify-center h-20 w-20 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 mb-6 shadow-inner">
            <ClipboardList className="h-10 w-10 text-gray-400 dark:text-gray-500" />
          </div>
          <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200">Bienvenido al Reporte General Pro-360</h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-md">
            Selecciona un Centro de Lavado y un rango de fechas en el formulario superior, luego presiona <strong>"Consultar"</strong> para generar el reporte.
          </p>
        </div>
      )}

      {/* Main Content Section */}
      {!loading && hasLoaded && <div className="space-y-8">
        {/* KPI Cards */}
        <ReportsSummaryCards
          receptionTotals={receptionTotals}
          washingTotals={washingTotals}
          washingAbortedTotals={washingAbortedTotals}
          washingAbortedKilos={washingAbortedKilos}
          dryingTotals={dryingTotals}
          dryingAbortedTotals={dryingAbortedTotals}
          dryingAbortedKilos={dryingAbortedKilos}
          kilosAlistados={alistamientoTotals.totalKilos || 0}
          utilityTotals={utilityTotals}
          utilityTypeTotals={utilityTypeTotals}
          workedHours={workedHours}
          programasLavado={programasLavado}
          kilosProcesados={kilosProcesados}
          kilosFinalizados={kilosFinalizados}
          loadingProg={loadingProg}
          washingByMachine={washingByMachine}
          washingAbortedByMachine={washingAbortedByMachine}
          productConsumption={productConsumption}
          pcLoading={pcLoading}
          productosPorPrograma={productosPorPrograma}
          startDate={startDate}
          endDate={endDate}
          laundryIndicators={selectedLaundry?.indicadores}
        />

        {/* ── Indicador de Planchado ── */}
        <Card className="relative overflow-hidden bg-white dark:bg-gray-800 shadow-sm border border-gray-200/80 dark:border-gray-700/80 rounded-2xl hover:shadow-lg transition-all duration-300">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500" />
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                <PackageCheck className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              </div>
              Indicador de Planchado
              {alistamientosLoading && <Loader2 className="h-4 w-4 animate-spin text-gray-400 ml-1" />}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Lenceria + Toallas lado a lado */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* Lenceria — prendas */}
              <div className="p-4 rounded-xl bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border border-purple-200 dark:border-purple-800/40">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-purple-500" />
                  <span className="text-xs font-semibold text-purple-700 dark:text-purple-300 uppercase tracking-wider">Planchado</span>
                </div>
                <p className="text-3xl font-bold text-purple-700 dark:text-purple-300 tabular-nums leading-tight">
                  {(alistamientoTotals.prendasLenceria || 0).toLocaleString('es-CO')}
                </p>
                <p className="text-xs text-purple-500 mt-0.5">prendas</p>
                <p className="text-sm font-semibold text-purple-600 dark:text-purple-400 mt-1 tabular-nums">
                  {(alistamientoTotals.totalKilosLenceria || 0).toFixed(1)} kg
                </p>
                <p className="text-xs text-purple-400 mt-0.5">{alistamientoTotals.lenceriaAlistamientos} alistamientos</p>
              </div>

              {/* Toallas — prendas */}
              <div className="p-4 rounded-xl bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border border-blue-200 dark:border-blue-800/40">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                  <span className="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wider">Doblado</span>
                </div>
                <p className="text-3xl font-bold text-blue-700 dark:text-blue-300 tabular-nums leading-tight">
                  {(alistamientoTotals.prendasToallas || 0).toLocaleString('es-CO')}
                </p>
                <p className="text-xs text-blue-500 mt-0.5">prendas</p>
                <p className="text-sm font-semibold text-blue-600 dark:text-blue-400 mt-1 tabular-nums">
                  {(alistamientoTotals.totalKilosToallas || 0).toFixed(1)} kg
                </p>
                <p className="text-xs text-blue-400 mt-0.5">{alistamientoTotals.toallasAlistamientos} alistamientos</p>
              </div>

              {/* Total alistado */}
              <div className="p-4 rounded-xl bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-700/40 dark:to-slate-700/40 border border-gray-200 dark:border-gray-600">
                <div className="flex items-center gap-2 mb-2">
                  <Layers className="h-3.5 w-3.5 text-gray-500" />
                  <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Total alistado</span>
                </div>
                <p className="text-3xl font-bold text-gray-900 dark:text-white tabular-nums leading-tight">
                  {(alistamientoTotals.totalPrendas || 0).toLocaleString('es-CO')}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">prendas</p>
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mt-1 tabular-nums">
                  {(alistamientoTotals.totalKilos || 0).toFixed(1)} kg
                </p>
                <p className="text-xs text-gray-400 mt-0.5">{alistamientoTotals.totalAlistamientos} alistamientos</p>
              </div>

              {/* Ratio alistado / procesado */}
              <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border border-emerald-200 dark:border-emerald-800/40">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />
                  <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 uppercase tracking-wider">Alist. / Proc.</span>
                </div>
                {(() => {
                  const alist = alistamientoTotals.totalKilos || 0;
                  const proc  = receptionTotals.kilosProcesados || 0;
                  const pct   = proc > 0 ? (alist / proc) * 100 : 0;
                  const color = pct >= 90 ? 'text-emerald-700 dark:text-emerald-300'
                              : pct >= 70 ? 'text-amber-600 dark:text-amber-400'
                              : 'text-red-600 dark:text-red-400';
                  return (
                    <>
                      <p className={`text-3xl font-bold tabular-nums leading-tight ${color}`}>
                        {proc > 0 ? `${pct.toFixed(1)}%` : '—'}
                      </p>
                      <p className="text-xs text-emerald-500 mt-0.5">kg alistados / kg lavados</p>
                      <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 mt-1 tabular-nums">
                        {alist.toFixed(1)} / {proc.toFixed(1)} kg
                      </p>
                    </>
                  );
                })()}
              </div>
            </div>

          </CardContent>
        </Card>

        {/* ── Mano de Obra — Planchado ── */}
        <Card className="relative overflow-hidden bg-white dark:bg-gray-800 shadow-sm border border-gray-200/80 dark:border-gray-700/80 rounded-2xl hover:shadow-lg transition-all duration-300">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 to-orange-500" />
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" />
                  Mano de Obra — Planchado
                </p>
                <p className="text-4xl font-bold text-amber-600 dark:text-amber-400 tabular-nums leading-tight">
                  {(() => {
                    const kg = alistamientoTotals.totalKilos || 0;
                    const h  = workedHours.horasAutorizadas  || 0;
                    return h > 0 ? (kg / h).toFixed(2) : '—';
                  })()}
                </p>
                <p className="text-sm text-amber-500 mt-1">kg planchados / hora autorizada</p>
                {workedHours.horasAutorizadas > 0 && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    {(alistamientoTotals.totalKilos || 0).toFixed(1)} kg ÷ {(workedHours.horasAutorizadas || 0).toFixed(1)} h
                  </p>
                )}
              </div>
              <div className="p-3 rounded-xl bg-amber-100 dark:bg-amber-900/30">
                <Clock className="h-6 w-6 text-amber-600 dark:text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Operational Analytics Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Horas trabajadas */}
          <Card className="lg:col-span-1 relative overflow-hidden bg-white dark:bg-gray-800 shadow-sm border border-gray-200/80 dark:border-gray-700/80 rounded-2xl hover:shadow-lg transition-all duration-300">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-slate-400 to-slate-600" />
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-slate-100 dark:bg-slate-800">
                  <Clock className="h-4 w-4 text-slate-600 dark:text-slate-400" />
                </div>
                Análisis de Horas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Horas trabajadas vs autorizadas */}
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-gradient-to-br from-slate-50 to-gray-100 dark:from-gray-700 dark:to-gray-750 rounded-xl border border-gray-100 dark:border-gray-600">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wider">Trabajadas</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">{Number(workedHours.horasTrabajadas || 0).toFixed(1)}</p>
                  <p className="text-xs text-gray-400">horas</p>
                </div>
                <div className="text-center p-4 bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-xl border border-indigo-100 dark:border-indigo-800/40">
                  <p className="text-xs font-medium text-indigo-600 dark:text-indigo-400 mb-1 uppercase tracking-wider">Autorizadas</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">{Number(workedHours.horasAutorizadas || 0).toFixed(1)}</p>
                  <p className="text-xs text-indigo-400 dark:text-indigo-500">horas</p>
                </div>
              </div>

              {/* Lavado */}
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700 space-y-1">
                <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-blue-500" />
                  Lavado
                </p>
                <div className="flex justify-between items-center p-2.5 rounded-lg bg-blue-50/60 dark:bg-blue-900/10 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Kg procesados / hora</span>
                  <span className="text-lg font-bold text-blue-700 dark:text-blue-300 tabular-nums">
                    {(() => {
                      const kg = Number(receptionTotals.kilosProcesados || 0);
                      const h  = Number(workedHours.horasAutorizadas || 0);
                      return (h > 0 ? kg / h : 0).toFixed(1);
                    })()}
                    <span className="text-sm font-normal text-gray-400 ml-1">kg/h</span>
                  </span>
                </div>
                <div className="flex justify-between items-center p-2.5 rounded-lg bg-blue-50/60 dark:bg-blue-900/10 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Kg finalizados / hora</span>
                  <span className="text-lg font-bold text-blue-700 dark:text-blue-300 tabular-nums">
                    {(() => {
                      const kg = Number(receptionTotals?.kilosFinalizados || 0);
                      const h  = Number(workedHours?.horasAutorizadas || 0);
                      return (h > 0 ? kg / h : 0).toFixed(1);
                    })()}
                    <span className="text-sm font-normal text-gray-400 ml-1">kg/h</span>
                  </span>
                </div>
              </div>

              {/* Planchado */}
              <div className="pt-3 border-t border-gray-200 dark:border-gray-700 space-y-1">
                <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
                  Planchado
                </p>
                <div className="flex justify-between items-center p-2.5 rounded-lg bg-amber-50/60 dark:bg-amber-900/10 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Kg alistados / hora</span>
                  <span className="text-lg font-bold text-amber-600 dark:text-amber-400 tabular-nums">
                    {(() => {
                      const kg = alistamientoTotals.totalKilos || 0;
                      const h  = Number(workedHours.horasAutorizadas || 0);
                      return (h > 0 ? kg / h : 0).toFixed(1);
                    })()}
                    <span className="text-sm font-normal text-gray-400 ml-1">kg/h</span>
                  </span>
                </div>
                <div className="flex justify-between items-center p-2.5 rounded-lg bg-purple-50/60 dark:bg-purple-900/10 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-purple-500 mr-1" />
                    Lenceria / hora
                  </span>
                  <span className="text-lg font-bold text-purple-600 dark:text-purple-400 tabular-nums">
                    {(() => {
                      const kg = alistamientoTotals.totalKilosLenceria || 0;
                      const h  = Number(workedHours.horasAutorizadas || 0);
                      return (h > 0 ? kg / h : 0).toFixed(1);
                    })()}
                    <span className="text-sm font-normal text-gray-400 ml-1">kg/h</span>
                  </span>
                </div>
                <div className="flex justify-between items-center p-2.5 rounded-lg bg-blue-50/40 dark:bg-blue-900/10 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400 mr-1" />
                    Toallas / hora
                  </span>
                  <span className="text-lg font-bold text-blue-500 dark:text-blue-400 tabular-nums">
                    {(() => {
                      const kg = alistamientoTotals.totalKilosToallas || 0;
                      const h  = Number(workedHours.horasAutorizadas || 0);
                      return (h > 0 ? kg / h : 0).toFixed(1);
                    })()}
                    <span className="text-sm font-normal text-gray-400 ml-1">kg/h</span>
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Serie diaria: Horas trabajadas vs autorizadas */}
          <Card className="lg:col-span-2 relative overflow-hidden bg-white dark:bg-gray-800 shadow-sm border border-gray-200/80 dark:border-gray-700/80 rounded-2xl hover:shadow-lg transition-all duration-300">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-400 to-cyan-500" />
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-emerald-50 dark:bg-emerald-900/30">
                  <BarChart3 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                </div>
                Tendencia Diaria de Horas
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                const dataWork = Array.isArray(dailyHoursSeries) ? dailyHoursSeries : [];
                const dataAuth = Array.isArray(dailyHoursAuthorized) ? dailyHoursAuthorized : [];
                const dataKg = Array.isArray(dailyKgPerAuthHour) ? dailyKgPerAuthHour : [];
                const dataAlist = Array.isArray(dailyAlistKgPerAuthHour) ? dailyAlistKgPerAuthHour : [];
                const values = [
                  ...(showHoursWork ? dataWork : []),
                  ...(showHoursAuth ? dataAuth : []),
                  ...(showKgPerAuth ? dataKg : []),
                  ...(showAlistKgPerAuth ? dataAlist : []),
                ].map(d => Number(d.value || 0));
                // Meta de eficiencia (kg/h) aplicable a lavado y planchado.
                const metaKg = (showKgPerAuth || showAlistKgPerAuth) ? Number(kgPerAuthMeta || 0) : 0;
                const max = Math.max(values.length ? Math.max(...values) : 0, metaKg);
                const min = 0; // horas no negativas
                const totalDaysCount = daysInRange?.length || 1;
                const w = Math.max(1200, totalDaysCount * 60); // ancho proporcional a los días
                const h = 300;  // alto svg (más alto para eje y labels)
                const pad = 12;
                const innerW = w - pad * 2;
                const bottomPad = 24; // espacio para eje X
                const innerH = h - pad * 2 - bottomPad;
                // Largo efectivo para distribuir puntos (usar la mayor longitud visible)
                const visibleLen = Math.max(
                  (daysInRange?.length || 0),
                  showHoursWork ? dataWork.length : 0,
                  showHoursAuth ? dataAuth.length : 0,
                  showKgPerAuth ? dataKg.length : 0,
                  showAlistKgPerAuth ? dataAlist.length : 0,
                );
                const n = Math.max(1, visibleLen - 1);
                const scaleX = (i) => pad + (innerW * i) / n;
                const scaleY = (v) => {
                  const range = (max - min) || 1;
                  return pad + innerH - ((v - min) / range) * innerH;
                };
                const path = (arr) => (arr.length ? arr.map((it, i) => {
                  const v = Number(it?.value || 0);
                  return `${i === 0 ? 'M' : 'L'} ${scaleX(i).toFixed(2)} ${scaleY(v).toFixed(2)}`;
                }).join(' ') : `M ${pad} ${pad + innerH} L ${w - pad} ${pad + innerH}`);
                const lastWork = Number(dataWork[dataWork.length - 1]?.value || 0);
                const lastAuth = Number(dataAuth[dataAuth.length - 1]?.value || 0);
                const lastKg = Number(dataKg[dataKg.length - 1]?.value || 0);
                const lastAlist = Number(dataAlist[dataAlist.length - 1]?.value || 0);
                const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + Number(b.value || 0), 0) / arr.length : 0);
                const avgWork = avg(dataWork);
                const avgAuth = avg(dataAuth);
                // Media = ratio global (Σ/Σ), consistente con la tabla de indicadores.
                const hrsAuthTotal = Number(workedHours?.horasAutorizadas || 0);
                const avgKg = hrsAuthTotal > 0 ? Number(kilosProcesados || 0) / hrsAuthTotal : 0;
                const avgAlist = hrsAuthTotal > 0 ? Number(alistamientoTotals?.totalKilos || 0) / hrsAuthTotal : 0;
                const totalDays = (daysInRange?.length || Math.max(dataWork.length, dataAuth.length));
                const tickEvery = 1; // mostrar ticks diarios siempre
                const showNumericLabels = totalDays <= 62; // valores numéricos hasta 2 meses
                const clampY = (y) => Math.max(pad + 6, y);
                if (values.length === 0) {
                  return (
                    <div>
                      <div className="flex flex-wrap items-center gap-4 text-xs mb-2">
                        <em className="text-muted-foreground">Selecciona al menos una serie para visualizar.</em>
                      </div>
                    </div>
                  );
                }
                return (
                  <div>
                    {/* Controles de visibilidad */}
                    <div className="flex flex-wrap items-center gap-3 text-xs mb-3">
                      <label className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border cursor-pointer select-none transition-all duration-200 ${
                        showHoursWork
                          ? 'bg-gray-50 dark:bg-gray-700/60 border-gray-200 dark:border-gray-600 shadow-sm'
                          : 'bg-transparent border-gray-100 dark:border-gray-700/40 opacity-50 hover:opacity-75'
                      }`}>
                        <input type="checkbox" className="sr-only" checked={showHoursWork} onChange={e => setShowHoursWork(e.target.checked)} />
                        <span className="inline-flex items-center gap-1.5">
                          <span className="inline-block w-2.5 h-2.5 rounded-full ring-1 ring-black/10" style={{ background: '#6b7280' }}></span>
                          <span className="font-medium text-gray-700 dark:text-gray-300">Trabajadas{showHoursWork ? ` (últ: ${lastWork.toFixed(2)} h)` : ''}</span>
                        </span>
                      </label>
                      <label className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border cursor-pointer select-none transition-all duration-200 ${
                        showHoursAuth
                          ? 'bg-gray-50 dark:bg-gray-700/60 border-gray-200 dark:border-gray-600 shadow-sm'
                          : 'bg-transparent border-gray-100 dark:border-gray-700/40 opacity-50 hover:opacity-75'
                      }`}>
                        <input type="checkbox" className="sr-only" checked={showHoursAuth} onChange={e => setShowHoursAuth(e.target.checked)} />
                        <span className="inline-flex items-center gap-1.5">
                          <span className="inline-block w-2.5 h-2.5 rounded-full ring-1 ring-black/10" style={{ background: '#0ea5e9' }}></span>
                          <span className="font-medium text-gray-700 dark:text-gray-300">Autorizadas{showHoursAuth ? ` (últ: ${lastAuth.toFixed(2)} h)` : ''}</span>
                        </span>
                      </label>
                      <label className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border cursor-pointer select-none transition-all duration-200 ${
                        showKgPerAuth
                          ? 'bg-gray-50 dark:bg-gray-700/60 border-gray-200 dark:border-gray-600 shadow-sm'
                          : 'bg-transparent border-gray-100 dark:border-gray-700/40 opacity-50 hover:opacity-75'
                      }`}>
                        <input type="checkbox" className="sr-only" checked={showKgPerAuth} onChange={e => setShowKgPerAuth(e.target.checked)} />
                        <span className="inline-flex items-center gap-1.5">
                          <span className="inline-block w-2.5 h-2.5 rounded-full ring-1 ring-black/10" style={{ background: '#10b981' }}></span>
                          <span className="font-medium text-gray-700 dark:text-gray-300">Lavado kg/h{showKgPerAuth ? ` (últ: ${lastKg.toFixed(2)})` : ''}</span>
                        </span>
                      </label>
                      <label className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border cursor-pointer select-none transition-all duration-200 ${
                        showAlistKgPerAuth
                          ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700 shadow-sm'
                          : 'bg-transparent border-gray-100 dark:border-gray-700/40 opacity-50 hover:opacity-75'
                      }`}>
                        <input type="checkbox" className="sr-only" checked={showAlistKgPerAuth} onChange={e => setShowAlistKgPerAuth(e.target.checked)} />
                        <span className="inline-flex items-center gap-1.5">
                          <span className="inline-block w-2.5 h-2.5 rounded-full ring-1 ring-black/10" style={{ background: '#f59e0b' }}></span>
                          <span className="font-medium text-amber-700 dark:text-amber-300">Planchado kg/h{showAlistKgPerAuth ? ` (últ: ${lastAlist.toFixed(2)})` : ''}</span>
                        </span>
                      </label>
                    </div>
                    <div className="w-full overflow-x-auto">
                    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
                      <rect x="0" y="0" width={w} height={h} rx="8" className="fill-transparent" />
                      {/* Líneas de promedio */}
                      {showHoursWork ? (<line x1={pad} x2={w - pad} y1={scaleY(avgWork)} y2={scaleY(avgWork)} stroke="#6b7280" strokeDasharray="4 4" opacity="0.5" />) : null}
                      {showHoursAuth ? (<line x1={pad} x2={w - pad} y1={scaleY(avgAuth)} y2={scaleY(avgAuth)} stroke="#0ea5e9" strokeDasharray="4 4" opacity="0.5" />) : null}
                      {showKgPerAuth ? (<line x1={pad} x2={w - pad} y1={scaleY(avgKg)} y2={scaleY(avgKg)} stroke="#10b981" strokeDasharray="4 4" opacity="0.5" />) : null}
                      {showAlistKgPerAuth ? (<line x1={pad} x2={w - pad} y1={scaleY(avgAlist)} y2={scaleY(avgAlist)} stroke="#f59e0b" strokeDasharray="4 4" opacity="0.5" />) : null}
                      {/* Meta de eficiencia (kg/h) — línea + etiqueta a la izquierda */}
                      {metaKg > 0 && (showKgPerAuth || showAlistKgPerAuth) ? (
                        <g>
                          <line x1={pad} x2={w - pad} y1={scaleY(metaKg)} y2={scaleY(metaKg)} stroke="#16a34a" strokeWidth="2" strokeDasharray="6 4" />
                          <text x={pad + 4} y={clampY(scaleY(metaKg)) - 4} fontSize="12" fontWeight="700" fill="#16a34a">Meta {metaKg} kg/h</text>
                        </g>
                      ) : null}
                      {/* Etiquetas de media (kg/h) a la derecha, separadas si coinciden */}
                      {showKgPerAuth ? (
                        <text x={w - pad - 4} y={clampY(scaleY(avgKg)) - 4} textAnchor="end" fontSize="12" fontWeight="700" fill="#10b981">Promedio P lavado {avgKg.toFixed(2)}</text>
                      ) : null}
                      {showAlistKgPerAuth ? (
                        <text x={w - pad - 4} y={clampY(scaleY(avgAlist)) - 4 - ((showKgPerAuth && Math.abs(scaleY(avgKg) - scaleY(avgAlist)) < 16) ? 16 : 0)} textAnchor="end" fontSize="12" fontWeight="700" fill="#f59e0b">Promedio P planchado {avgAlist.toFixed(2)}</text>
                      ) : null}
                      {/* Sparkline trabajadas */}
                      {showHoursWork ? (<path d={path(dataWork)} fill="none" stroke="#6b7280" strokeWidth="2.5" />) : null}
                      {/* Sparkline autorizadas */}
                      {showHoursAuth ? (<path d={path(dataAuth)} fill="none" stroke="#0ea5e9" strokeWidth="2.5" />) : null}
                      {/* Sparkline kg/h lavado */}
                      {showKgPerAuth ? (<path d={path(dataKg)} fill="none" stroke="#10b981" strokeWidth="2.5" />) : null}
                      {/* Sparkline kg/h planchado */}
                      {showAlistKgPerAuth ? (<path d={path(dataAlist)} fill="none" stroke="#f59e0b" strokeWidth="2.5" strokeDasharray="6 3" />) : null}
                      {/* Puntos finales */}
                      {showHoursWork && dataWork.length > 0 ? (
                        <circle cx={scaleX(dataWork.length - 1)} cy={scaleY(Number(dataWork[dataWork.length - 1]?.value || 0))} r="4" fill="#6b7280" stroke="#374151" />
                      ) : null}
                      {showHoursAuth && dataAuth.length > 0 ? (
                        <circle cx={scaleX(dataAuth.length - 1)} cy={scaleY(Number(dataAuth[dataAuth.length - 1]?.value || 0))} r="4" fill="#0ea5e9" stroke="#075985" />
                      ) : null}
                      {showKgPerAuth && dataKg.length > 0 ? (
                        <circle cx={scaleX(dataKg.length - 1)} cy={scaleY(Number(dataKg[dataKg.length - 1]?.value || 0))} r="4" fill="#10b981" stroke="#065f46" />
                      ) : null}
                      {showAlistKgPerAuth && dataAlist.length > 0 ? (
                        <circle cx={scaleX(dataAlist.length - 1)} cy={scaleY(Number(dataAlist[dataAlist.length - 1]?.value || 0))} r="4" fill="#f59e0b" stroke="#92400e" />
                      ) : null}
                      {/* Eje X y labels de fechas + valores en ticks */}
                      {/* Línea del eje X */}
                      <line x1={pad} x2={w - pad} y1={pad + innerH + 4} y2={pad + innerH + 4} stroke="#cbd5e1" />
                      {(daysInRange || []).map((d, i) => {
                        if (i % tickEvery !== 0 && i !== (daysInRange.length - 1)) return null;
                        const x = scaleX(i);
                        const yAxis = pad + innerH + 4;
                        const vw = Number(dataWork?.[i]?.value || 0);
                        const va = Number(dataAuth?.[i]?.value || 0);
                        const vk = Number(dataKg?.[i]?.value || 0);
                        const vp = Number(dataAlist?.[i]?.value || 0);
                        return (
                          <g key={`tick-${d}`}>
                            {/* Tick */}
                            <line x1={x} x2={x} y1={yAxis} y2={yAxis + 4} stroke="#94a3b8" />
                            {/* Fecha */}
                            <text x={x} y={yAxis + 18} fontSize="13" fill="#64748b" textAnchor="middle">{String(d).slice(5)}</text>
                            {/* Etiquetas de valores sobre los puntos en el tick */}
                            {showNumericLabels && showHoursWork ? (
                              <text x={x} y={clampY(scaleY(vw) - 10)} fontSize="14" fontWeight="600" fill="#374151" textAnchor="middle">{vw.toFixed(1)}</text>
                            ) : null}
                            {showNumericLabels && showHoursAuth ? (
                              <text x={x} y={clampY(scaleY(va) - 10 - (showHoursWork ? 16 : 0))} fontSize="14" fontWeight="600" fill="#075985" textAnchor="middle">{va.toFixed(1)}</text>
                            ) : null}
                            {showNumericLabels && showKgPerAuth ? (
                              <text x={x} y={clampY(scaleY(vk) - 10 - ((showHoursWork ? 16 : 0) + (showHoursAuth ? 16 : 0)))} fontSize="14" fontWeight="600" fill="#065f46" textAnchor="middle">{vk.toFixed(2)}</text>
                            ) : null}
                            {showNumericLabels && showAlistKgPerAuth ? (
                              <text x={x} y={clampY(scaleY(vp) - 10 - ((showHoursWork ? 16 : 0) + (showHoursAuth ? 16 : 0) + (showKgPerAuth ? 16 : 0)))} fontSize="14" fontWeight="600" fill="#92400e" textAnchor="middle">{vp.toFixed(2)}</text>
                            ) : null}
                          </g>
                        );
                      })}
                    </svg>
                    </div>
                    {/*          <div className="mt-1 text-[11px] text-gray-500 flex items-center gap-4">
                    {showHoursWork ? (<span>Prom. trabajadas: {avgWork.toFixed(2)} h</span>) : null}
                    {showHoursAuth ? (<span>Prom. autorizadas: {avgAuth.toFixed(2)} h</span>) : null}
                    {showKgPerAuth ? (<span>Prom. kg/h autorizada: {avgKg.toFixed(2)} kg/h</span>) : null}
                  </div> */}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </div>

        {/* Charts Section */}
{/*         <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Análisis Gráfico</h2>
          <VerticalBarsGrid title="Resumen Operacional" chartData={chartData} maxChartValue={maxChartValue} />
        </div> */}

        {/* Daily Trends Section */}
        <div className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/80 dark:border-gray-700/80 p-6 md:p-8">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500" />
          <div className="flex items-center gap-3 mb-8">
            <div className="flex items-center justify-center h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-md shadow-blue-200 dark:shadow-blue-900/40">
              <BarChart3 className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Tendencias Diarias</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Análisis de series temporales por día</p>
            </div>
          </div>

          {/* 1) Recepción, Ciclos y Rechazo */}
          <div className="mb-8">
            <DailyBars
              title="Recepción, Ciclos y Rechazo"
              daysInRange={daysInRange}
              holidays={holidaysMap}
              series={[
                { key: 'rec', label: 'Recepción (Kg)', color: '#10b981', data: dailyReception, showLabel: true },
                { key: 'cyc', label: 'Ciclos', color: '#3b82f6', data: dailyCycles, showLabel: true },
                { key: 'rej', label: 'Rechazo (Kg)', color: '#ef4444', data: dailyRejection, scale: 5, showLabel: true },
                { key: 'rejPct', label: 'Rechazo (%)', color: '#f97316', data: dailyRejectionPct, showLabel: true, labelFormat: (v) => `${Number(v).toFixed(1)}%` },
              ]}
              scalableKeys={['rec', 'cyc', 'rej', 'rejPct']}
              defaultVisibleKeys={['rec', 'cyc', 'rejPct']}
              
            />

          </div>

          {/* 2) Recepción y Servicios */}
          <div className="mb-8">
            <DailyBars
              title={servicesPerKg ? 'Servicios por Kilogramo' : 'Recepción y Servicios'}
              daysInRange={daysInRange}
              holidays={holidaysMap}
              series={servicesPerKg ? servicesSeriesPerKg : servicesSeriesAbs}
              scalableKeys={servicesPerKg ? ['aguaKg', 'gasKg', 'elecKg'] : ['rec', 'agua', 'gas', 'elec']}
              perSeriesNormalize
              headroomRatio={1.2}
              rightControls={(
                <label className="text-xs md:text-sm inline-flex items-center gap-2 text-gray-600 dark:text-gray-400">
                  <input
                    type="checkbox"
                    className="accent-gray-600"
                    checked={servicesPerKg}
                    onChange={(e) => setServicesPerKg(e.target.checked)}
                  />
                  Mostrar consumo por kg
                </label>
              )}
              labelSeriesKey={servicesPerKg ? undefined : 'rec'}
              labelFormatter={!servicesPerKg ? (v) => `${Number(v).toFixed(1)} Kg` : undefined}
              showValueLabels={servicesPerKg}
              valueFormatter={servicesPerKg ? (v) => `${Number(v).toFixed(3)}` : undefined}
            />
          </div>

          {/* 3) Recepción y Horas */}
          <div className="mb-8">
            <DailyBars
              title={hoursEfficiencyPer ? 'Eficiencia por Hora Autorizada' : 'Recepción y Horas'}
              daysInRange={daysInRange}
              holidays={holidaysMap}
              series={hoursEfficiencyPer ? [
                { key: 'kgPerAuth', label: 'Kg por hora autorizada (kg/h)', color: '#6b7280', data: dailyKgPerAuthHour, scale: 1 },
              ] : [
                { key: 'rec', label: 'Recepción (Kg)', color: '#6366f1', data: dailyReception, group: 'kg' },
                { key: 'hrs', label: 'Horas (h)', color: '#f59e0b', data: dailyHoursSeries, group: 'h' },
                { key: 'hrsAuth', label: 'Horas autorizadas (h)', color: '#0ea5e9', data: dailyHoursAuthorized, scale: 1, group: 'h' },
              ]}
              scalableKeys={hoursEfficiencyPer ? ['kgPerAuth'] : ['rec', 'hrs', 'hrsAuth']}
              perSeriesNormalize
              headroomRatio={1.2}
              referenceLines={hoursEfficiencyPer ? [
                ...(kgPerAuthMeta > 0 ? [{ value: kgPerAuthMeta, label: `Meta ${kgPerAuthMeta} kg/h`, color: '#16a34a', seriesKey: 'kgPerAuth', dash: true }] : []),
                ...(kgPerAuthMedia > 0 ? [{ value: kgPerAuthMedia, label: `Promedio P ${kgPerAuthMedia.toFixed(2)} kg/h`, color: '#2563eb', seriesKey: 'kgPerAuth', dash: true }] : []),
              ] : []}
              footer={hoursEfficiencyPer ? (
                <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-[11px] md:text-xs text-gray-600 dark:text-gray-300">
                  {kgPerAuthMeta > 0 ? (
                    <span className="inline-flex items-center gap-1.5">
                      <span className="inline-block w-2.5 h-0.5 bg-green-600" /> Meta: <b className="text-green-700 dark:text-green-400">{kgPerAuthMeta} kg/h</b>
                    </span>
                  ) : null}
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block w-2.5 h-0.5 bg-blue-600" /> Promedio P (global): <b className={kgPerAuthMeta > 0 ? (kgPerAuthMedia >= kgPerAuthMeta ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400') : ''}>{kgPerAuthMedia.toFixed(2)} kg/h</b>
                  </span>
                  {kgPerAuthMeta > 0 ? (() => {
                    const diff = kgPerAuthMedia - kgPerAuthMeta;
                    const pct = (diff / kgPerAuthMeta) * 100;
                    const ok = diff >= 0;
                    const cls = ok ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400';
                    return (
                      <span>Dif. vs meta: <b className={cls}>{`${ok ? '+' : ''}${diff.toFixed(2)} kg/h (${ok ? '+' : ''}${pct.toFixed(1)}%)`}</b></span>
                    );
                  })() : null}
                  <span>Máx: <b>{kgPerAuthStats.max.toFixed(2)}</b></span>
                  <span>Mín: <b>{kgPerAuthStats.min.toFixed(2)}</b></span>
                  <span>Último: <b>{kgPerAuthStats.last.toFixed(2)}</b></span>
                  <span className="text-gray-400">({kgPerAuthStats.activeDays} días con actividad)</span>
                </div>
              ) : null}
              rightControls={(
                <div className="flex items-center gap-3">
                  <label className="text-xs md:text-sm inline-flex items-center gap-2 text-gray-600 dark:text-gray-400">
                    <input
                      type="checkbox"
                      className="accent-gray-600"
                      checked={hoursEfficiencyPer}
                      onChange={(e) => setHoursEfficiencyPer(e.target.checked)}
                    />
                    Mostrar kg/h autorizada
                  </label>
                  <label className="text-xs md:text-sm inline-flex items-center gap-2 text-gray-600 dark:text-gray-400" title="Por defecto se excluyen los conductores del cálculo de horas">
                    <input
                      type="checkbox"
                      className="accent-indigo-600"
                      checked={includeConductor}
                      onChange={(e) => setIncludeConductor(e.target.checked)}
                    />
                    Incluir conductores
                  </label>
                </div>
              )}
              showValueLabels={true}
              valueFormatter={hoursEfficiencyPer ? (v) => `${Number(v).toFixed(3)}` : (v) => `${Number(v).toFixed(1)}`}
            />
          </div>
        </div>
      </div>}

      {/* Modal Enviar por correo */}
      <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
        <DialogContent className="max-w-2xl bg-white dark:bg-gray-800 border border-gray-200/80 dark:border-gray-700/80 rounded-2xl shadow-xl">
          <DialogHeader className="border-b border-gray-200 dark:border-gray-700 pb-4">
            <DialogTitle className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-3">
              <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 shadow-sm">
                <Send className="h-4 w-4 text-white" />
              </div>
              Enviar Reporte por Correo
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Selector de administradores */}
            <div>
              <label className="text-sm font-medium mb-2 block text-gray-900 dark:text-white">
                Seleccionar Administradores
              </label>
              {loadingAdmins ? (
                <div className="text-sm text-gray-500 dark:text-gray-400">Cargando usuarios admin...</div>
              ) : (
                <div className="space-y-3">
                  <div className="flex gap-2 mb-3">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={selectAllAdmins}
                      disabled={adminUsers.length === 0}
                      className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                      Seleccionar todos
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={clearAdminSelection}
                      disabled={selectedAdmins.length === 0}
                      className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                      Limpiar selección
                    </Button>
                  </div>
                  <div className="max-h-32 overflow-y-auto border rounded-md p-2 space-y-1">
                    {adminUsers.map((admin) => (
                      <label key={admin._id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={selectedAdmins.some(a => a._id === admin._id)}
                          onChange={() => toggleAdminSelection(admin)}
                          className="rounded"
                        />
                        <span className="text-sm">{admin.nombre} ({admin.correo})</span>
                      </label>
                    ))}
                    {adminUsers.length === 0 && !loadingAdmins && (
                      <div className="text-sm text-gray-500 text-center py-2">No se encontraron usuarios admin</div>
                    )}
                  </div>
                  {selectedAdmins.length > 0 && (
                    <div className="text-xs text-blue-600">
                      {selectedAdmins.length} administrador{selectedAdmins.length > 1 ? 'es' : ''} seleccionado{selectedAdmins.length > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Separador */}
            <div className="flex items-center">
              <div className="flex-1 border-t border-gray-200"></div>
              <span className="px-3 text-xs text-gray-500 bg-white">O</span>
              <div className="flex-1 border-t border-gray-200"></div>
            </div>

            {/* Email manual */}
            <div>
              <label className="text-sm font-medium">Correo manual</label>
              <Input
                type="email"
                placeholder="correo@empresa.com"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                className="mt-1"
              />
            </div>

            {/* Asunto */}
            <div>
              <label className="text-sm font-medium">Asunto</label>
              <Input
                type="text"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <Button
              variant="outline"
              onClick={() => setEmailModalOpen(false)}
              disabled={emailSending}
              className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSendEmail}
              disabled={emailSending || (selectedAdmins.length === 0 && !emailTo)}
              className="bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-200"
            >
              {emailSending ? 'Enviando…' : 'Enviar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </motion.div>
  );
};

export default ReportsPage;
